import BaseDivider from "../../../../components/elements/BaseDivider"
import BaseModalPage from "../../../../components/elements/BaseModalPage"
import BaseSectionHeadline from "../../../../components/elements/BaseSectionHeadline"
import { loadWithExpiryFromLocalStorage, minutesToHoursAndMinutes, transformSecondsToMinutes } from "../../../../services/functions"
import { OptimizeRouteResultSessionMetricType, OptimizeRouteResultType } from "../../types"
import { useNavigate } from "react-router-dom"
import { Navigation } from "../../../../api/constants"
import dayjs from "dayjs"
import { useEffect, useState } from "react"

type RouteOptimizationModalScreen2Props = {
    visible: boolean
    resultPage?: OptimizeRouteResultType
}

export default function RouteOptimizationModalScreen2(props: RouteOptimizationModalScreen2Props) {
    const [resultPage, setResultPage] = useState<OptimizeRouteResultType>()
    const navigate = useNavigate()

    useEffect(() => {
        if (localStorage.getItem("resultOfOptimization")) {
            setResultPage(loadWithExpiryFromLocalStorage("resultOfOptimization"))
        }
    }, [])
    useEffect(() => {
        if (props.resultPage && (Object.keys(props.resultPage).length !== 0 || !resultPage)) {
            setResultPage(props.resultPage)
        }
    }, [props.resultPage, resultPage])

    return (
        <BaseModalPage visible={props.visible}>
            <p className="text-xs text-gray-400">
                Optimierter Kalender: {resultPage?.optimizedFor.employeeName} | Kalenderwoche: {resultPage?.optimizedFor.weekNumber}/
                {resultPage?.optimizedFor.year} | Globale Zeiteinstellungen:{" "}
                {(resultPage as OptimizeRouteResultType)?.usedWeekDayConfig?.globalStartTime} -{" "}
                {(resultPage as OptimizeRouteResultType)?.usedWeekDayConfig?.globalEndTime}
            </p>
            <BaseSectionHeadline label="Optimierte Wochentage" classProps="!mb-0 mt-4" />
            {(!(resultPage as OptimizeRouteResultType)?.optimizationResult ||
                (resultPage as OptimizeRouteResultType)?.optimizationResult?.length === 0) && (
                <p className="text-xs text-gray-400 pt-2">Keine Termine wurden optimiert</p>
            )}
            {(resultPage as OptimizeRouteResultType)?.optimizationResult?.map((item) => (
                <OptimizedResultElement {...item} />
            ))}
            <BaseDivider />
            <BaseSectionHeadline label="Konflikte" classProps="!mb-0 mt-4" />
            {(!(resultPage as OptimizeRouteResultType)?.collisionMatrix ||
                (resultPage as OptimizeRouteResultType)?.collisionMatrix?.length === 0) && (
                <p className="text-xs text-gray-400 pt-2">Keine Konflikte wurden gefunden</p>
            )}
            {(resultPage as OptimizeRouteResultType)?.collisionMatrix?.map((item) => (
                <CollisionElement
                    {...item}
                    onClick={() => navigate(`${Navigation.CALENDAR + item.conflictingEmployeeId}?sessionId=${item.conflictingSessionId}`)}
                />
            ))}
            <BaseDivider />
            <BaseSectionHeadline label="Übersprungene Termine" classProps="!mb-0 mt-4" />
            {(!(resultPage as OptimizeRouteResultType)?.skippedSessions ||
                (resultPage as OptimizeRouteResultType)?.skippedSessions?.length === 0) && (
                <p className="text-xs text-gray-400 pt-2">Keine Termine wurden übersprungen</p>
            )}
            {(resultPage as OptimizeRouteResultType)?.skippedSessions?.map((item) => (
                <SkippedSessionElement {...item} onClick={() => navigate(`${Navigation.CALENDAR + item.attendees[0]}?sessionId=${item._id}`)} />
            ))}
        </BaseModalPage>
    )
}

type OptimizedResultElementProps = {
    dayId: string
    metrics: OptimizeRouteResultSessionMetricType
    overScheduledHours: number
}

function OptimizedResultElement(props: OptimizedResultElementProps) {
    return (
        <div className="flex flex-col gap-2 px-4 pt-4">
            <h3 className="text-base font-medium">
                {props.dayId === "monday"
                    ? "Montag"
                    : props.dayId === "tuesday"
                    ? "Dienstag"
                    : props.dayId === "wednesday"
                    ? "Mittwoch"
                    : props.dayId === "thursday"
                    ? "Donnerstag"
                    : "Freitag"}
            </h3>
            <div className="flex flex-row flex-wrap gap-8 justify-start pb-2">
                <ResultElement label="Termine" value={props.metrics.sessionCount} />
                <ResultElement
                    label="Fahrzeit"
                    value={minutesToHoursAndMinutes(transformSecondsToMinutes(props.metrics.totalTravelDurationSeconds))}
                />
                <ResultElement label="Strecke in km" value={(props.metrics.totalTravelDistanceMeters / 1000).toFixed(2) + " km"} />
                <ResultElement
                    label="Pausenzeit"
                    value={minutesToHoursAndMinutes(transformSecondsToMinutes(props.metrics.totalBreakDurationSeconds))}
                />
                <ResultElement
                    label="Arbeitszeit"
                    value={minutesToHoursAndMinutes(transformSecondsToMinutes(props.metrics.totalVisitDurationSeconds))}
                />
                <ResultElement label="Gesamtdauer" value={minutesToHoursAndMinutes(transformSecondsToMinutes(props.metrics.totalDurationSeconds))} />
            </div>
        </div>
    )
}

type ResultElementProps = {
    width?: string
    label: string
    value: number | string
    truncate?: boolean
}

function ResultElement(props: ResultElementProps) {
    return (
        <div className={`flex flex-col ${props.width ? props.width : ""}`}>
            <p className="text-xs  font-semibold">{props.label}</p>
            <p className={`text-xs text-gray-400 ${props.truncate ? "truncate" : ""}`}>{props.value}</p>
        </div>
    )
}

type SkippedSessionElementType = {
    _id: string
    title: string
    startAt: Date
    endAt: Date
    message: string
    onClick: () => void
}

function SkippedSessionElement(props: SkippedSessionElementType) {
    return (
        <div onClick={props.onClick} className="flex flex-row flex-wrap justify-start gap-8 py-2">
            <ResultElement label="Titel" value={props.title} />
            <ResultElement label="Start" value={props.startAt.toLocaleString()} />
            <ResultElement label="Ende" value={props.endAt.toLocaleString()} />
            <ResultElement label="Nachricht" value={props.message} />
        </div>
    )
}

type CollisionElementProps = {
    conflictingSessionId: string
    conflictingSessionTitle: string
    conflictingEmployeeId: string
    conflictingEmployeeName: string
    conflictingSessionStartAt: Date
    conflictingSessionEndAt: Date
    optimizedSessionStartAt: Date
    optimizedSessionEndAt: Date
    optimizedSessionTitle: string
    optimizedSessionAttendees: {
        employeeId: string
        employeeName: string
    }[]
    optimizedSessionId: string
    onClick: () => void
}

function CollisionElement(props: CollisionElementProps) {
    return (
        <div className="flex flex-col gap-1 mt-2 border-l-2 border-primary pl-2">
            <div onClick={props.onClick} className="flex flex-row flex-wrap gap-8 border-b py-2">
                <ResultElement width="w-80" truncate label="Konflikttermin:" value={props.conflictingSessionTitle} />
                <ResultElement width="w-40" truncate label="Mitarbeiter:" value={props.conflictingEmployeeName} />
                <ResultElement label="Start:" value={dayjs(props.conflictingSessionStartAt).format("DD.MM.YYYY HH:mm") + " Uhr"} />
                <ResultElement label="Ende:" value={dayjs(props.conflictingSessionEndAt).format("DD.MM.YYYY HH:mm") + " Uhr"} />
            </div>
            <div onClick={props.onClick} className="flex flex-row flex-wrap gap-8 py-2">
                <ResultElement width="w-80" truncate label="Optimierter Termin:" value={props.optimizedSessionTitle} />
                <ResultElement
                    width="w-40"
                    truncate
                    label="Mitarbeiter:"
                    value={props.optimizedSessionAttendees.map((attendee) => attendee.employeeName).join(", ")}
                />
                <ResultElement label="Start:" value={dayjs(props.optimizedSessionStartAt).format("DD.MM.YYYY HH:mm") + " Uhr"} />
                <ResultElement label="Ende:" value={dayjs(props.optimizedSessionEndAt).format("DD.MM.YYYY HH:mm") + " Uhr"} />
            </div>
        </div>
    )
}
